import { Box, styled } from '@mui/material';

interface MediaTextContainerProps {
  verticalAlignment?: 'top' | 'center' | 'bottom';
  mediaPosition?: 'left' | 'right';
  mediaWidth?: number;
  isStackedOnMobile?: boolean;
}

interface MediaContainerProps {
  imageFill?: boolean;
  focalPoint?: {
    x: number;
    y: number;
  };
}

interface ContentContainerProps {
  verticalAlignment?: 'top' | 'center' | 'bottom';
}

// Helper to filter out custom props
const shouldForwardMediaTextProp = (prop: string) =>
  ![
    'verticalAlignment',
    'mediaPosition',
    'mediaWidth',
    'isStackedOnMobile',
  ].includes(prop);

const shouldForwardMediaProp = (prop: string) =>
  !['imageFill', 'focalPoint'].includes(prop);

const shouldForwardContentProp = (prop: string) =>
  !['verticalAlignment'].includes(prop);

export const StyledMediaTextContainer = styled(Box, {
  shouldForwardProp: shouldForwardMediaTextProp,
})<MediaTextContainerProps>(
  ({
    verticalAlignment,
    mediaPosition = 'left',
    mediaWidth = 50,
    isStackedOnMobile = true,
  }) => {
    const baseColumns =
      mediaPosition === 'right' ? `auto ${mediaWidth}%` : `${mediaWidth}% auto`;

    return {
      display: 'grid',
      gridTemplateColumns: baseColumns,
      alignItems: verticalAlignment || 'center',
      gap: '2rem',
      '@media (max-width: 600px)': {
        gridTemplateColumns: isStackedOnMobile ? '1fr' : baseColumns,
        gap: isStackedOnMobile ? '1rem' : '2rem',
      },
    };
  }
);

export const StyledMediaContainer = styled(Box, {
  shouldForwardProp: shouldForwardMediaProp,
})<MediaContainerProps>(({ imageFill, focalPoint }) => ({
  position: 'relative',
  height: '100%',
  ...(imageFill && {
    '& img, & video': {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      ...(focalPoint && {
        objectPosition: `${focalPoint.x * 100}% ${focalPoint.y * 100}%`,
      }),
    },
  }),
}));

export const StyledContentContainer = styled(Box, {
  shouldForwardProp: shouldForwardContentProp,
})<ContentContainerProps>(({ verticalAlignment }) => {
  let justifyContent = 'center';
  if (verticalAlignment === 'top') {
    justifyContent = 'flex-start';
  } else if (verticalAlignment === 'bottom') {
    justifyContent = 'flex-end';
  }

  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent,
    padding: '0',
  };
});
